/* ==========================================================
   BR STUDIO — sticky bottom player
   The queue comes from the theme customiser (Footer → Music
   player), emitted as window.BRS_PLAYLIST by sections/player.liquid.
   Cross-page continuity: track index, position, volume, shuffle,
   repeat and mute persist to localStorage and are restored on
   the next page, so playback picks up where it left off.

   An uploaded file plays through a native <audio> element with
   full transport. SoundCloud gets the same through its widget
   API. Spotify, Deezer, Apple Music and Audiomack embed with
   their own transport; our queue controls still work.
   ========================================================== */
(function () {
  const KEY = 'brs.player.v1';
  const SC_API = 'https://w.soundcloud.com/player/api.js';
  const CFG = window.BRS_PLAYER || {};
  const FOLLOW = CFG.follow || {};
  const WATCH = CFG.watch || {};

  // Words the visitor reads come from window.BRS.t (layout/theme.liquid), in their
  // language; the English here is only the fallback. {name} slots are filled from o.
  const T = (k, f, o) => {
    const d = window.BRS && window.BRS.t;
    const s = (d && d[k]) || f;
    return o ? s.replace(/\{(\w+)\}/g, (m, n) => (n in o ? o[n] : m)) : s;
  };

  /* Merchant text and links land in innerHTML, so they all go through here. */
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* YouTube shows a real subscribe confirmation when the channel URL carries
     sub_confirmation=1, so add it if the merchant pasted a plain channel link.
     Spotify has no equivalent — that link just opens the artist page. */
  const FOLLOW_URL = (() => {
    const u = (FOLLOW.url || '').trim();
    if (!u) return '';
    if (/youtube\.com|youtu\.be/i.test(u) && !/sub_confirmation=/i.test(u)) {
      return u + (u.indexOf('?') === -1 ? '?' : '&') + 'sub_confirmation=1';
    }
    return u;
  })();
  const FOLLOW_ICON = /youtube\.com|youtu\.be/i.test(FOLLOW_URL) ? 'youtube' : 'spotify';

  /* "Watch video" is per track: each Track block can carry its own link, and
     this section-level one is the fallback for the tracks that do not.
     player.liquid blanks it when the switch is off. The button only opens the
     link; nothing is embedded, so no third-party player loads into the page. */
  const WATCH_URL = (WATCH.url || '').trim();
  const WATCH_LABEL = (WATCH.label || '').trim() || 'Watch video';
  const WATCH_THUMB = WATCH.thumb !== false;

  const ICON = {
    play: '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>',
    pause: '<svg viewBox="0 0 24 24"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>',
    stop: '<svg viewBox="0 0 24 24"><path d="M6 6h12v12H6z"/></svg>',
    next: '<svg viewBox="0 0 24 24"><path d="M6 5l9 7-9 7zM17 5h2v14h-2z"/></svg>',
    prev: '<svg viewBox="0 0 24 24"><path d="M18 5l-9 7 9 7zM5 5h2v14H5z"/></svg>',
    vol: '<svg viewBox="0 0 24 24"><path d="M4 9h3l4-4v14l-4-4H4zM15.5 8.5a4.5 4.5 0 010 7v-2a2.5 2.5 0 000-3z"/></svg>',
    spotify: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="M7 9.5c3.2-1 8-.8 10.5 1M7.6 13c2.6-.8 6.4-.6 8.6 1M8.4 16.3c2-.5 4.6-.4 6.2.6"/></svg>',
    add: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>',
    youtube: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="4"/><path d="M10 9.2l5.2 2.8L10 14.8z" fill="currentColor" stroke="none"/></svg>',
    shuffle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/></svg>',
    repeat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>',
    repeatOne: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/><text x="12" y="15.5" font-size="9" font-family="sans-serif" text-anchor="middle" fill="currentColor" stroke="none">1</text></svg>',
    mute: '<svg viewBox="0 0 24 24"><path d="M4 9h3l4-4v14l-4-4H4z"/><path d="M16 9.5l4 5M20 9.5l-4 5" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>',
    watch: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><circle cx="12" cy="12" r="9.2"/><path d="M10.2 8.4l6 3.6-6 3.6z" fill="currentColor" stroke="none"/></svg>',
    queue: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6h11M4 12h11M4 18h7M18 5v10"/><circle cx="16" cy="16" r="2.4" fill="currentColor" stroke="none"/></svg>'
  };

  /* Platform labels double as the source badge in the UI. */
  const LABEL = {
    file: 'Studio', soundcloud: 'SoundCloud', spotify: 'Spotify', deezer: 'Deezer',
    apple: 'Apple Music', audiomack: 'Audiomack'
  };

  /* Embed heights each platform's widget actually needs. */
  const EMBED_H = { spotify: 152, deezer: 92, apple: 175, audiomack: 252 };

  const provider = (u) => !u ? 'file'
    : /soundcloud\.com/i.test(u) ? 'soundcloud'
    : /spotify\.com/i.test(u) ? 'spotify'
    : /deezer\.com/i.test(u) ? 'deezer'
    : /music\.apple\.com/i.test(u) ? 'apple'
    : /audiomack\.com/i.test(u) ? 'audiomack' : 'unknown';

  function embedSrc(url) {
    const p = provider(url);
    if (p === 'spotify') {
      const m = url.match(/(track|album|playlist|episode|show)\/([A-Za-z0-9]+)/);
      return m ? `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=brstudio` : null;
    }
    if (p === 'deezer') {
      const m = url.match(/(track|album|playlist)\/(\d+)/);
      return m ? `https://widget.deezer.com/widget/dark/${m[1]}/${m[2]}` : null;
    }
    if (p === 'apple') {
      // Apple's embed is the same URL on the embed. subdomain — path and the
      // ?i= track selector carry over untouched.
      return url.replace(/^https?:\/\/(embed\.)?music\.apple\.com/i, 'https://embed.music.apple.com');
    }
    if (p === 'audiomack') {
      if (/audiomack\.com\/embed\//i.test(url)) return url;
      const m = url.match(/audiomack\.com\/([^\/?#]+)\/(song|album|playlist)\/([^\/?#]+)/i);
      return m ? `https://audiomack.com/embed/${m[2].toLowerCase()}/${m[1]}/${m[3]}` : null;
    }
    return null;
  }

  const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60), r = Math.floor(s % 60);
    return m + ':' + String(r).padStart(2, '0');
  };

  function loadState() {
    try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
  }

  /* ---------- artwork auto-load ----------
     Artwork set in the customiser wins. Otherwise ask the platform for it.
     Measured from a real storefront: SoundCloud resolves through the widget
     API and Apple Music through the iTunes lookup endpoint. Spotify, Deezer
     and Audiomack refuse the browser request (no CORS header — Spotify simply
     hangs), so those keep the branded tile in the bar; their embed panel above
     it renders its own cover art anyway. The calls stay in for the day those
     endpoints open up. Hits are cached in localStorage so other pages do not
     refetch, and every failure is silent. */
  const ART_KEY = 'brs.art.v1';
  let artCache = {};
  try { artCache = JSON.parse(localStorage.getItem(ART_KEY)) || {}; } catch (e) {}
  const artSave = () => { try { localStorage.setItem(ART_KEY, JSON.stringify(artCache)); } catch (e) {} };

  function artApi(p, u) {
    const e = encodeURIComponent(u);
    if (p === 'spotify') return 'https://open.spotify.com/oembed?url=' + e;
    if (p === 'soundcloud') return 'https://soundcloud.com/oembed?format=json&url=' + e;
    if (p === 'deezer') return 'https://api.deezer.com/oembed?format=json&url=' + e;
    if (p === 'audiomack') return 'https://audiomack.com/oembed?format=json&url=' + e;
    if (p === 'apple') {
      // ?i= is the track id on an album URL; otherwise the trailing id is the album
      const m = u.match(/[?&]i=(\d+)/) || u.match(/\/(\d{6,})(?:[/?#]|$)/);
      return m ? 'https://itunes.apple.com/lookup?id=' + m[1] : null;
    }
    return null;
  }

  function pickArt(j) {
    if (!j) return null;
    if (j.thumbnail_url) return j.thumbnail_url;
    const r = j.results && j.results[0];
    if (r && r.artworkUrl100) return r.artworkUrl100.replace('100x100', '400x400');
    return null;
  }

  function fetchArtwork(url, p) {
    if (artCache[url]) return Promise.resolve(artCache[url]);
    const api = artApi(p, url);
    if (!api) return Promise.resolve(null);
    // a blocked endpoint can hang rather than reject, so cap the wait
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    return fetch(api, { mode: 'cors', credentials: 'omit', signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { const a = pickArt(j); if (a) { artCache[url] = a; artSave(); } return a; })
      .catch(() => null)
      .finally(() => clearTimeout(timer));
  }

  /* ---------- video thumbnail ----------
     YouTube publishes stills at a plain image URL, so no request of ours is
     needed. Vimeo needs one oEmbed call, which does send CORS headers; the hit
     is cached next to the track artwork. Any other host gets no thumbnail and
     the button alone stands in for it. */
  function ytId(u) {
    const m = String(u).match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  }

  function vimeoId(u) {
    const m = String(u).match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
  }

  function videoThumb(url) {
    const y = ytId(url);
    if (y) return Promise.resolve('https://i.ytimg.com/vi/' + y + '/mqdefault.jpg');
    const v = vimeoId(url);
    if (!v) return Promise.resolve(null);
    if (artCache[url]) return Promise.resolve(artCache[url]);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    return fetch('https://vimeo.com/api/oembed.json?url=' + encodeURIComponent('https://vimeo.com/' + v),
      { mode: 'cors', credentials: 'omit', signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const a = j && j.thumbnail_url;
        if (a) { artCache[url] = a; artSave(); }
        return a || null;
      })
      .catch(() => null)
      .finally(() => clearTimeout(timer));
  }

  function build() {
    const saved = loadState();
    const queue = (Array.isArray(window.BRS_PLAYLIST) ? window.BRS_PLAYLIST.slice() : [])
      .filter((t) => t && (t.src || t.url));

    const el = document.createElement('div');
    el.className = 'brs-player';
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', T('pl_region', 'Audio player'));
    el.innerHTML = `
      <div class="hero-smoke brs-player__smoke" data-hero-smoke aria-hidden="true"></div>
      <div class="brs-player__now">
        <div class="brs-player__art" data-art><img alt="" data-artimg hidden /></div>
        <div class="brs-player__txt">
          <div class="brs-player__title" data-title>${T('pl_nothing', 'Nothing queued')}</div>
          <div class="brs-player__meta"><span class="brs-player__artist" data-artist>Black Rout Studios</span></div>
        </div>
      </div>
      <div class="brs-player__mid">
        <div class="brs-player__ctrls">
          <button class="brs-btn" data-prev aria-label="${esc(T('pl_prev', 'Previous track'))}">${ICON.prev}</button>
          <button class="brs-btn brs-btn--play" data-play aria-label="${esc(T('pl_play', 'Play'))}">${ICON.play}</button>
          <button class="brs-btn" data-stop aria-label="${esc(T('pl_stop', 'Stop'))}">${ICON.stop}</button>
          <button class="brs-btn" data-next aria-label="${esc(T('pl_next', 'Next track'))}">${ICON.next}</button>
          <button class="brs-btn brs-btn--sm" data-shuffle aria-label="${esc(T('pl_shuffle', 'Shuffle'))}" aria-pressed="false">${ICON.shuffle}</button>
          <button class="brs-btn brs-btn--sm" data-repeat aria-label="${esc(T('pl_repeat_off', 'Repeat off'))}">${ICON.repeat}</button>
          <div class="brs-player__bar" data-bar role="slider" aria-label="${esc(T('pl_seek', 'Seek'))}" tabindex="0"><span data-fill></span></div>
          <div class="brs-player__time" data-time>0:00 / 0:00</div>
        </div>
      </div>
      <div class="brs-player__right">
        <div class="brs-player__vol" data-volwrap><button class="brs-vol-btn" data-mute aria-label="${esc(T('pl_mute', 'Mute'))}">${ICON.vol}</button><input type="range" min="0" max="100" value="80" data-vol aria-label="${esc(T('pl_volume', 'Volume'))}" /></div>
        <div class="brs-player__note" data-note hidden></div>
      </div>
      <div class="brs-player__actions">
        <button class="brs-player__btn brs-player__queuebtn" data-queuebtn aria-label="${esc(T('pl_show_list', 'Show playlist'))}" aria-expanded="false">${ICON.queue}<span>${esc(T('pl_playlist', 'Playlist'))}</span></button>
        <a class="brs-player__btn brs-player__save" data-save target="_blank" rel="noopener" hidden
           aria-label="${esc(T('pl_spotify_aria', 'Open this track on Spotify'))}">${ICON.spotify}<span>${esc(T('pl_spotify', 'Open on Spotify'))}</span></a>
        ${(WATCH_URL || queue.some((t) => t.video)) ? `<a class="brs-player__btn brs-player__watch" data-watch
           href="${esc(WATCH_URL)}" target="_blank" rel="noopener" ${WATCH_URL ? '' : 'hidden'}
           aria-label="${esc(WATCH_LABEL)}">${ICON.watch}<span>${esc(WATCH_LABEL)}</span></a>` : ''}
        ${FOLLOW_URL ? `<a class="brs-player__btn brs-player__follow" href="${esc(FOLLOW_URL)}" target="_blank" rel="noopener"
           aria-label="${esc(FOLLOW.label || 'Follow on YouTube')}">
           ${ICON[FOLLOW_ICON]}<span>${esc(FOLLOW.label || 'Follow on YouTube')}</span></a>` : ''}
      </div>`;
    document.body.appendChild(el);

    /* hero-smoke.js scans for hosts on DOMContentLoaded, and its listener runs
       before this one — the bar did not exist yet. Mount its canvas by hand. */
    if (window.BRSHeroSmoke) window.BRSHeroSmoke.mount(el.querySelector('.brs-player__smoke'));

    const embed = document.createElement('div');
    embed.className = 'brs-player__embed';
    embed.hidden = true;
    document.body.appendChild(embed);

    const scHost = document.createElement('div');
    scHost.className = 'brs-player__sc';
    document.body.appendChild(scHost);

    const q = (s) => el.querySelector(s);
    const ui = {
      art: q('[data-art]'), img: q('[data-artimg]'), title: q('[data-title]'),
      artist: q('[data-artist]'), play: q('[data-play]'),
      stop: q('[data-stop]'), next: q('[data-next]'), prev: q('[data-prev]'),
      bar: q('[data-bar]'), fill: q('[data-fill]'), time: q('[data-time]'),
      vol: q('[data-vol]'), volWrap: q('[data-volwrap]'), note: q('[data-note]'),
      save: q('[data-save]'),
      shuffle: q('[data-shuffle]'), repeat: q('[data-repeat]'), mute: q('[data-mute]'),
      queueBtn: q('[data-queuebtn]'), watch: q('[data-watch]')
    };

    /* Playlist panel — tracks grouped by the Album field on each block. */
    const panel = document.createElement('div');
    panel.className = 'brs-queue';
    panel.hidden = true;
    document.body.appendChild(panel);

    /* Every track can carry its own video; the section-level link is the
       fallback for the ones that do not. Stills are resolved once per distinct
       link and land independently, so re-render whenever one arrives and the
       panel is open. A link we get no frame from simply has no thumbnail. */
    const videoThumbs = {};
    if (WATCH_THUMB) {
      const links = [WATCH_URL].concat(queue.map((t) => (t.video || '').trim()));
      Array.from(new Set(links.filter(Boolean))).forEach((u) => {
        videoThumb(u).then((src) => {
          if (!src) return;
          videoThumbs[u] = src;
          if (!panel.hidden) renderQueue();
        });
      });
    }

    /* Autoplay unless the visitor explicitly paused on an earlier page.
       saved.playing === undefined means this is a first visit. */
    const wantPlay = saved.playing === undefined ? !!CFG.autoplay : !!saved.playing;

    const st = {
      queue,
      i: Math.min(saved.i || 0, Math.max(0, queue.length - 1)),
      pos: saved.pos || 0,
      playing: false,
      wantPlay,
      vol: typeof saved.vol === 'number' ? saved.vol : 80,
      dur: 0,
      widget: null,
      shuffle: !!saved.shuffle,
      repeat: saved.repeat || 'off',      // off | all | one
      muted: !!saved.muted,
      preMute: typeof saved.preMute === 'number' ? saved.preMute : 80
    };
    ui.vol.value = st.vol;

    // if a fetched cover 404s, fall back to the branded tile rather than a gap
    ui.img.addEventListener('error', () => {
      ui.img.hidden = true; ui.img.removeAttribute('src');
      ui.art.classList.add('is-fallback');
    });

    const save = () => {
      try {
        localStorage.setItem(KEY, JSON.stringify({
          i: st.i, pos: st.pos, playing: st.playing, vol: st.vol,
          shuffle: st.shuffle, repeat: st.repeat, muted: st.muted, preMute: st.preMute
        }));
      } catch (e) {}
    };

    const track = () => st.queue[st.i];

    function setEnabled(on) {
      [ui.play, ui.stop, ui.next, ui.prev].forEach((b) => (b.disabled = !on));
    }

    /* Browsers refuse programmatic playback until the visitor has interacted
       with the page, so a blocked autoplay arms the next click/tap/keypress. */
    let armed = false;
    function armGesture() {
      if (armed) return;
      armed = true;
      const go = () => {
        document.removeEventListener('pointerdown', go, true);
        document.removeEventListener('keydown', go, true);
        armed = false;
        if (st.wantPlay && !st.playing && st.widget) {
          try { st.widget.play(); } catch (e) {}
        }
      };
      document.addEventListener('pointerdown', go, true);
      document.addEventListener('keydown', go, true);
    }

    function paintMeta(t, sound) {
      // p picks the fallback tile's initials and the artwork lookup. An
      // uploaded file is its own source — a Spotify link on the same block is
      // there for the Open button, so it must not stand in as the source.
      const p = t ? (t.src ? 'file' : provider(t.url)) : null;
      ui.title.textContent = (sound && sound.title) || (t && t.title) || (t ? T('pl_untitled_track', 'Untitled track') : T('pl_nothing', 'Nothing queued'));
      ui.artist.textContent = (sound && sound.user && sound.user.username) || (t && t.artist) || 'Black Rout Studios';
      const artUrl = (sound && sound.artwork_url ? sound.artwork_url.replace('-large', '-t200x200') : null) || (t && t.artwork);
      if (artUrl) {
        ui.img.src = artUrl; ui.img.hidden = false;
        ui.art.classList.remove('is-fallback'); ui.art.removeAttribute('data-mono');
      } else {
        // branded fallback tile while we go looking for the real cover
        ui.img.hidden = true; ui.img.removeAttribute('src');
        ui.art.classList.add('is-fallback');
        ui.art.setAttribute('data-mono', (p || 'br').slice(0, 2).toUpperCase());
        if (t && t.url && !t.src) {
          fetchArtwork(t.url, p).then((a) => {
            if (!a || track() !== t) return;   // queue moved on while we waited
            ui.img.src = a; ui.img.hidden = false;
            ui.art.classList.remove('is-fallback'); ui.art.removeAttribute('data-mono');
          });
        }
      }
    }

    function paintPlaying(on) {
      st.playing = on;
      ui.play.innerHTML = on ? ICON.pause : ICON.play;
      ui.play.setAttribute('aria-label', on ? T('pl_pause', 'Pause') : T('pl_play', 'Play'));
      ui.art.classList.toggle('is-playing', on);
      save();
    }

    function paintTime() {
      const pct = st.dur ? Math.min(100, (st.pos / st.dur) * 100) : 0;
      ui.fill.style.width = pct + '%';
      ui.time.textContent = fmt(st.pos) + ' / ' + fmt(st.dur);
    }

    /* ---------- uploaded file transport (native <audio>) ----------
       Wrapped in the same play/pause/seekTo/setVolume shape the SoundCloud
       widget exposes, so toggle, stop, the seek bar and the volume slider all
       drive it without a second code path. */
    const el_audio = document.createElement('audio');
    el_audio.preload = 'metadata';
    el_audio.style.display = 'none';
    document.body.appendChild(el_audio);

    const fileWidget = {
      play: () => { const p = el_audio.play(); if (p && p.catch) p.catch(() => armGesture()); },
      pause: () => el_audio.pause(),
      seekTo: (ms) => { el_audio.currentTime = ms / 1000; },
      setVolume: (v) => { el_audio.volume = Math.max(0, Math.min(1, v / 100)); }
    };

    el_audio.addEventListener('loadedmetadata', () => { st.dur = el_audio.duration || 0; paintTime(); });
    el_audio.addEventListener('timeupdate', () => { st.pos = el_audio.currentTime; paintTime(); });
    el_audio.addEventListener('play', () => paintPlaying(true));
    el_audio.addEventListener('pause', () => paintPlaying(false));
    el_audio.addEventListener('ended', () => advance());
    el_audio.addEventListener('error', () => {
      // clearing the src fires error too — only report a genuinely bad file
      if (!el_audio.getAttribute('src')) return;
      ui.title.textContent = (track() && track().title) || T('pl_unavailable', 'Track unavailable');
      ui.artist.textContent = T('pl_check_file', 'Check the filename in Content → Files');
    });

    function mountFile(t, autoplay, seekTo) {
      st.widget = fileWidget;
      st.dur = 0;
      el_audio.src = t.src;
      el_audio.volume = Math.max(0, Math.min(1, (st.muted ? 0 : st.vol) / 100));
      el_audio.load();
      // no metadata in an uploaded file, so fall back to the filename
      const named = Object.assign({}, t);
      if (!named.title) {
        named.title = decodeURIComponent(t.src.split('/').pop().split('?')[0])
          .replace(/\.[a-z0-9]+$/i, '').replace(/[_-]+/g, ' ');
      }
      paintMeta(named);
      if (!panel.hidden) renderQueue();
      if (seekTo) {
        const go = () => { el_audio.currentTime = seekTo; el_audio.removeEventListener('loadedmetadata', go); };
        el_audio.addEventListener('loadedmetadata', go);
      }
      if (autoplay) { fileWidget.play(); armGesture(); }
    }

    /* ---------- SoundCloud transport ---------- */
    function mountSC(t, autoplay, seekTo) {
      scHost.innerHTML = '';
      const f = document.createElement('iframe');
      f.allow = 'autoplay';
      f.src = 'https://w.soundcloud.com/player/?url=' + encodeURIComponent(t.url) +
        '&auto_play=' + (autoplay ? 'true' : 'false') +
        '&hide_related=true&show_comments=false&show_user=true&visual=false';
      scHost.appendChild(f);
      const w = window.SC.Widget(f);
      st.widget = w;
      w.bind(window.SC.Widget.Events.READY, () => {
        w.setVolume(st.muted ? 0 : st.vol);
        w.getCurrentSound((s) => paintMeta(t, s));
        w.getDuration((d) => { st.dur = d / 1000; paintTime(); });
        if (seekTo) w.seekTo(seekTo * 1000);
        if (autoplay) { w.play(); armGesture(); }
      });
      w.bind(window.SC.Widget.Events.PLAY, () => paintPlaying(true));
      w.bind(window.SC.Widget.Events.PAUSE, () => paintPlaying(false));
      w.bind(window.SC.Widget.Events.FINISH, () => advance());
      w.bind(window.SC.Widget.Events.PLAY_PROGRESS, (e) => {
        st.pos = e.currentPosition / 1000;
        paintTime();
      });
    }

    function ensureSC(cb) {
      if (window.SC && window.SC.Widget) return cb();
      let s = document.querySelector('script[data-sc-api]');
      if (!s) {
        s = document.createElement('script');
        s.src = SC_API; s.async = true; s.setAttribute('data-sc-api', '');
        document.head.appendChild(s);
      }
      s.addEventListener('load', cb, { once: true });
    }

    /* ---------- platform embeds (Spotify / Deezer / Apple / Audiomack) ---------- */
    function mountEmbed(t) {
      const src = embedSrc(t.url);
      const p = provider(t.url);
      st.widget = null;
      st.dur = 0; st.pos = 0; paintTime();
      if (!src) { embed.hidden = true; paintMeta(t); return; }
      embed.innerHTML = `<iframe src="${src}" height="${EMBED_H[p] || 152}" allow="autoplay *; encrypted-media *; clipboard-write" loading="lazy" title="${LABEL[p] || 'Player'}"></iframe>`;
      embed.hidden = false;
      paintMeta(t);
      // these platforms own their transport; ours only manages the queue
      ui.play.disabled = true; ui.stop.disabled = true;
      ui.vol.disabled = true; ui.volWrap.style.opacity = '.35';
      ui.note.hidden = false;
      ui.note.textContent = T('pl_controls', '{p} controls play & volume in its own panel →', { p: LABEL[p] || T('pl_platform', 'This platform') });
      paintPlaying(false);
    }

    function load(idx, autoplay, seekTo) {
      if (!st.queue.length) return;
      st.i = (idx + st.queue.length) % st.queue.length;
      const t = track();
      st.pos = seekTo || 0;
      setEnabled(true);
      ui.note.hidden = true;
      /* Opens the song on Spotify. Shown whenever the block carries a Spotify
         link — including on an uploaded file, where the link is the same song
         on their service. */
      if (ui.save) {
        const spotifyUrl = provider(t.url) === 'spotify' ? t.url : '';
        ui.save.hidden = !spotifyUrl;
        if (spotifyUrl) ui.save.href = spotifyUrl;
      }
      /* This song's own video wins; the section-level link stands in for the
         tracks that have none. */
      if (ui.watch) {
        const v = (t.video || '').trim() || WATCH_URL;
        ui.watch.hidden = !v;
        if (v) ui.watch.href = v;
      }
      ui.vol.disabled = false; ui.volWrap.style.opacity = '';
      if (t.src) {
        embed.hidden = true; embed.innerHTML = '';
        scHost.innerHTML = '';
        mountFile(t, autoplay, seekTo);
      } else if (provider(t.url) === 'soundcloud') {
        el_audio.pause(); el_audio.removeAttribute('src');
        embed.hidden = true; embed.innerHTML = '';
        ensureSC(() => mountSC(t, autoplay, seekTo));
      } else {
        el_audio.pause(); el_audio.removeAttribute('src');
        if (st.widget) { try { st.widget.pause(); } catch (e) {} }
        scHost.innerHTML = '';
        mountEmbed(t);
      }
      if (!panel.hidden) renderQueue();
      save();
    }

    function toggle() {
      if (!st.widget) return;
      st.wantPlay = !st.playing;
      st.playing ? st.widget.pause() : st.widget.play();
    }
    function stopAll() {
      if (!st.widget) return;
      st.wantPlay = false;
      st.widget.pause();
      st.widget.seekTo(0);
      st.pos = 0; paintTime(); paintPlaying(false);
    }
    function pickNext() {
      if (st.shuffle && st.queue.length > 1) {
        let n = st.i;
        while (n === st.i) n = Math.floor(Math.random() * st.queue.length);
        return n;
      }
      return st.i + 1;
    }
    function next() { st.wantPlay = true; load(pickNext(), true); }
    function prev() { st.wantPlay = true; load(st.i - 1, true); }

    /* End of a track: repeat-one replays it, repeat-all and shuffle roll on,
       and with repeat off we stop rather than looping the queue forever. */
    function advance() {
      if (st.repeat === 'one') { load(st.i, true); return; }
      const atEnd = !st.shuffle && st.i === st.queue.length - 1;
      if (atEnd && st.repeat === 'off') { st.wantPlay = false; paintPlaying(false); return; }
      next();
    }

    /* ---------- shuffle / repeat / mute ---------- */
    function paintModes() {
      ui.shuffle.classList.toggle('is-on', st.shuffle);
      ui.shuffle.setAttribute('aria-pressed', String(st.shuffle));
      ui.repeat.classList.toggle('is-on', st.repeat !== 'off');
      ui.repeat.innerHTML = st.repeat === 'one' ? ICON.repeatOne : ICON.repeat;
      ui.repeat.setAttribute('aria-label',
        st.repeat === 'one' ? T('pl_repeat_one', 'Repeat this track') : st.repeat === 'all' ? T('pl_repeat_all', 'Repeat all') : T('pl_repeat_off', 'Repeat off'));
      ui.mute.innerHTML = st.muted ? ICON.mute : ICON.vol;
      ui.mute.setAttribute('aria-label', st.muted ? T('pl_unmute', 'Unmute') : T('pl_mute', 'Mute'));
      ui.mute.classList.toggle('is-on', st.muted);
    }

    function applyVolume() {
      const v = st.muted ? 0 : st.vol;
      ui.vol.value = v;
      if (st.widget) st.widget.setVolume(v);
    }

    ui.shuffle.addEventListener('click', () => { st.shuffle = !st.shuffle; paintModes(); save(); });
    ui.repeat.addEventListener('click', () => {
      st.repeat = st.repeat === 'off' ? 'all' : st.repeat === 'all' ? 'one' : 'off';
      paintModes(); save();
    });
    ui.mute.addEventListener('click', () => {
      if (st.muted) { st.muted = false; st.vol = st.preMute || 80; }
      else { st.muted = true; st.preMute = st.vol; }
      applyVolume(); paintModes(); save();
    });

    /* ---------- playlist panel ---------- */
    function renderQueue() {
      const fallbackThumb = WATCH_URL ? videoThumbs[WATCH_URL] : null;
      const video = fallbackThumb ? `
        <div class="brs-queue__group">
          <div class="brs-queue__album">Video</div>
          <a class="brs-queue__vrow" href="${esc(WATCH_URL)}" target="_blank" rel="noopener">
            <img class="brs-queue__vimg" src="${esc(fallbackThumb)}" alt="" />
            <span class="brs-queue__vlabel">${esc(WATCH_LABEL)}</span>
            ${ICON.watch}
          </a>
        </div>` : '';
      if (!st.queue.length) { panel.innerHTML = video + '<div class="brs-queue__empty">' + esc(T('pl_empty', 'Nothing queued yet.')) + '</div>'; return; }
      // group by album, keeping the order the merchant set in the customiser
      const groups = [];
      st.queue.forEach((t, idx) => {
        const name = (t.album || '').trim() || T('pl_singles', 'Singles');
        let g = groups.find((x) => x.name === name);
        if (!g) { g = { name, rows: [] }; groups.push(g); }
        g.rows.push({ t, idx });
      });
      panel.innerHTML = video + groups.map((g) => `
        <div class="brs-queue__group">
          <div class="brs-queue__album">${esc(g.name)}</div>
          ${g.rows.map(({ t, idx }) => {
            /* The thumbnail is a sibling of the row, never a child: the row is a
               <button> that jumps to the track, and a link inside a button is
               invalid and unclickable. */
            const v = (t.video || '').trim();
            const thumb = v ? videoThumbs[v] : null;
            return `
            <div class="brs-queue__item">
              <button class="brs-queue__row${idx === st.i ? ' is-current' : ''}" data-idx="${idx}">
                <span class="brs-queue__n">${String(idx + 1).padStart(2, '0')}</span>
                <span class="brs-queue__t">${esc(t.title || T('pl_untitled', 'Untitled'))}</span>
                <span class="brs-queue__a">${esc(t.artist || '')}</span>
              </button>
              ${thumb ? `<a class="brs-queue__vlink" href="${esc(v)}" target="_blank" rel="noopener"
                 aria-label="${esc(WATCH_LABEL)}"><img src="${esc(thumb)}" alt="" /></a>` : ''}
            </div>`;
          }).join('')}
        </div>`).join('');
    }

    function toggleQueue(show) {
      const open = typeof show === 'boolean' ? show : panel.hidden;
      if (open) renderQueue();
      panel.hidden = !open;
      ui.queueBtn.setAttribute('aria-expanded', String(open));
      ui.queueBtn.classList.toggle('is-on', open);
    }

    ui.queueBtn.addEventListener('click', (e) => { e.stopPropagation(); toggleQueue(); });
    panel.addEventListener('click', (e) => {
      const row = e.target.closest('[data-idx]');
      if (!row) return;
      st.wantPlay = true;
      load(parseInt(row.dataset.idx, 10), true);
      toggleQueue(false);
    });
    document.addEventListener('click', (e) => {
      if (panel.hidden) return;
      if (!panel.contains(e.target) && !ui.queueBtn.contains(e.target)) toggleQueue(false);
    });

    ui.play.addEventListener('click', toggle);
    ui.stop.addEventListener('click', stopAll);
    ui.next.addEventListener('click', next);
    ui.prev.addEventListener('click', prev);
    ui.vol.addEventListener('input', () => {
      st.vol = +ui.vol.value;
      if (st.vol > 0 && st.muted) st.muted = false;   // dragging the slider unmutes
      st.preMute = st.vol;
      if (st.widget) st.widget.setVolume(st.vol);
      paintModes();
      save();
    });
    ui.bar.addEventListener('click', (e) => {
      if (!st.widget || !st.dur) return;
      const r = ui.bar.getBoundingClientRect();
      const to = ((e.clientX - r.left) / r.width) * st.dur;
      st.widget.seekTo(to * 1000);
      st.pos = to; paintTime();
    });

    // keep position fresh for the next page
    setInterval(save, 1000);
    window.addEventListener('pagehide', save);
    window.addEventListener('beforeunload', save);

    /* ---------- public API ----------
       Other sections start a specific song by name — the Work page cards do
       this. Matching is deliberately loose: a card reads "Django — Mad
       Laganda" while the Track block is just "Django", so we try exact, then
       prefix, then substring, on a punctuation-stripped comparison. */
    window.BRSPlayer = {
      play(query) {
        const norm = (s) => String(s == null ? '' : s)
          .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        const want = norm(query);
        if (!want || !st.queue.length) return false;
        const names = st.queue.map((t) => norm(t.title));
        let i = names.indexOf(want);
        if (i < 0) i = names.findIndex((n) => n && (n.startsWith(want) || want.startsWith(n)));
        if (i < 0) i = names.findIndex((n) => n && (n.includes(want) || want.includes(n)));
        if (i < 0) return false;
        st.wantPlay = true;
        load(i, true);
        armGesture();
        return true;
      },
      titles() { return st.queue.map((t) => t.title || ''); }
    };

    paintModes();
    applyVolume();

    if (!st.queue.length) {
      setEnabled(false);
      paintMeta(null);   // paints the fallback tile, then we overwrite the copy
      ui.title.textContent = T('pl_nothing', 'Nothing queued');
      ui.artist.textContent = T('pl_add_hint', 'Add tracks in Theme editor → Footer → Music player');
      return;
    }
    // resume where the last page left off, or autoplay from the top
    load(st.i, st.wantPlay, st.pos);
    if (st.wantPlay) armGesture();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
    setTimeout(boot, 900);               // fallback: blocked font/CSS request
  } else boot();

  function boot() {
    if (window.__brsPlayerMounted) return;   // survive the double-boot race
    window.__brsPlayerMounted = true;
    build();
  }
})();
